import {
  Fragment, useEffect, useMemo, useState,
} from 'react';

// Third-party
import Box from '@mui/material/Box';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import LoadingButton from '@mui/lab/LoadingButton';
import SvgIcon from '@mui/material/SvgIcon';

// Components
import { getSessionValue } from '@mgm/common/utils';
import { MemoizedBaseAvatar, BaseCheckbox } from '@mgm/common/components/base';
import { SubmissionBlockWrapper } from '../../Shared';
import { NoteDialog } from './NoteDialog';

// Redux-toolkit
import { getSelectedDailySubmission } from '../../../../store/tasklists/selectors';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';

// Hooks
import { useAppSelector } from '../../../../hooks/useAppStore';
import { useUpdateSubmissionAPI } from '../../Shared/hooks/api';

// Types
import { TSubmission, TSubmissionTask } from '../../../../types';

// Assets
import { ReactComponent as NoteIcon } from '../../Shared/static/ic_note.svg';

// Utils
import { isTasksChange } from '../../Shared/utils';

const DailySubmissionBlock = () => {
  const { mutate: updateSubmissionMutate, isLoading } = useUpdateSubmissionAPI();
  const selectedDailySubmission = useAppSelector(getSelectedDailySubmission);
  const { personnel } = useAppSelector(getAuthenticateUser);

  const [tasks, setTasks] = useState<Array<TSubmissionTask>>([]);
  const [submissionNote, setSubmissionNote] = useState(selectedDailySubmission?.note || '');
  const [isOpenNoteDialog, setIsOpenDialog] = useState(false);

  useEffect(() => {
    setTasks([...(selectedDailySubmission?.tasks || [])]);
    setSubmissionNote(selectedDailySubmission.note);
  }, [selectedDailySubmission?.tasks]);

  const isBtnDisabled = useMemo(() => {
    const prevSubmissions = getSessionValue('prev', 'sessionStorage') as Array<TSubmission>;
    let isDisable: boolean;

    const currentSubmission = prevSubmissions?.find((submission) => submission.tasklistId === selectedDailySubmission.tasklistId);

    isDisable = isTasksChange(tasks, currentSubmission?.tasks || []).length === 0;

    if (currentSubmission?.note?.localeCompare(submissionNote) !== 0) {
      isDisable = false;
    }

    return isDisable;
  }, [tasks, submissionNote]);

  const changeTask = (index: number) => {
    const updatedTasks = [...tasks];

    updatedTasks[index] = {
      ...updatedTasks[index],
      isDone: !updatedTasks[index].isDone,
    };

    setTasks(updatedTasks);
  };

  const updateSubmission = () => {
    const submission = {
      note: submissionNote,
      submissionId: selectedDailySubmission.id,
      tasks,
    };

    updateSubmissionMutate(submission);
  };

  const openNoteDialog = () => {
    setIsOpenDialog(true);
  };

  return (
    <SubmissionBlockWrapper selectedSubmission={selectedDailySubmission}>
      <Box>
        <CardHeader
          sx={{
            px: 3,
          }}
          title={selectedDailySubmission?.tasklistName}
          action={(
            <Stack marginTop={1} direction="row" spacing={2}>
              <SvgIcon
                className="cursor-pointer"
                onClick={openNoteDialog}
                fontSize="large"
                component={NoteIcon}
                inheritViewBox
              />
              <LoadingButton
                onClick={updateSubmission}
                disabled={isBtnDisabled}
                loading={isLoading}
                size="small"
                variant="contained"
              >
                update
              </LoadingButton>
            </Stack>
            )}
        />
        <Box sx={{ px: 3, py: 1 }}>
          {tasks?.map((task, index) => (
            <Fragment key={task.name}>
              <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                <FormControlLabel
                  control={(
                    <BaseCheckbox
                      handleClick={() => changeTask(index)}
                      value={task}
                      checked={task.isDone || false}
                    />
                      )}
                  label={(
                    <Typography
                      variant="body2"
                      sx={{
                        ...(task.isDone && {
                          color: 'text.disabled',
                          textDecoration: 'line-through',
                        }),
                      }}
                    >
                      {task.name}
                    </Typography>
                      )}
                />
                {task.isDone && (
                  <MemoizedBaseAvatar
                    avatarName={personnel.name}
                    avatarFile={personnel.avatarFile}
                  />
                )}
              </Stack>
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Fragment>
          ))}
        </Box>
      </Box>
      <NoteDialog
        isOpenNoteDialog={isOpenNoteDialog}
        handleClose={() => setIsOpenDialog(false)}
        submissionNote={submissionNote}
        setSubmissionNote={setSubmissionNote}
      />
    </SubmissionBlockWrapper>
  );
};

export {
  DailySubmissionBlock,
};
